/**
 * F13.5 commit 3 + F-cascade-display-fix (2026-05-26) + F-cascade-use-amounts
 * (2026-05-27) — Cascade de comissão para a coluna 11 da tabela /dossiers.
 *
 * Renderiza 2, 3 ou 4 linhas conforme presença de cliente/intermediário:
 *
 *   Cliente      35%   1 256,50  ← só aparece se clientAmount > 0
 *   Téc          35%   1 256,50
 *   Interm.      25%     897,50  ← só aparece se intermAmount > 0
 *   Euro Storm    5%     179,50  ← top-border lime/15
 *
 * F-cascade-use-amounts (2026-05-27): valor de cada camada lê o amount V3
 * direto do banco (clientAmount, technicianAmount, intermediaryAmount,
 * eurostormAmount já calculados pelo calculateCommission no backend).
 * Antes calculava `value = facturado × pct/100`, o que dava errado em base
 * 'budget' onde commission é sobre orcamento (e facturado é o post-retenção,
 * menor). Agora os valores batem com o que está armazenado.
 *
 * Modo legacy (commissionModel === 'legacy'): a tabela não rastreia amounts
 * V3 — usa `legacyTechAmount` direto como valor do técnico, deriva tecPct =
 * amount / facturado × 100.
 *
 * Fallback: se um amount vier null/undefined (linha antiga sem fix), cai pra
 * fórmula antiga (facturado × pct/100) — não-regressivo.
 */

import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";

const PCT_FMT = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });

interface Props {
  facturado: number | string | null | undefined;
  /**
   * F8 — % do cliente sobre o orçamento bruto. Só > 0 em base 'budget'
   * (em 'invoiced' cliente fica fora do split e este campo é 0).
   */
  clientPct?: number | string | null | undefined;
  techPct: number | string | null | undefined;
  intermPct?: number | string | null | undefined;
  /**
   * F8 — % do eurostorm efetivo (resíduo). Quando informado, é usado direto;
   * quando ausente, é derivado como 100 − cliente − tech − interm.
   */
  eurostormPct?: number | string | null | undefined;
  /**
   * F-cascade-use-amounts — amounts V3 já calculados no banco. Quando
   * presentes, usados diretamente como valor em €. Ausentes → fallback
   * (facturado × pct/100).
   */
  clientAmount?: number | string | null | undefined;
  techAmount?: number | string | null | undefined;
  intermAmount?: number | string | null | undefined;
  eurostormAmount?: number | string | null | undefined;
  intermName?: string | null;
  /**
   * F-cascade-montage — parte de OUTRO técnico no mesmo carro (montador),
   * mostrada numa linha própria com a % dele. Ausente/0 → sem linha.
   */
  otherTechAmount?: number | string | null | undefined;
  otherTechPct?: number | string | null | undefined;
  otherTechName?: string | null;
  commissionModel?: string | null | undefined;
  /**
   * F-cascade-hide-client-invoiced — base do cálculo ('budget' | 'invoiced').
   * Em 'invoiced' (faturado) o cliente NÃO participa do split — a remise já
   * saiu do cálculo (devis − facturé) — então a linha "Cliente" é OMITIDA
   * (mostrar "0% + remise" confundia). Em 'budget' o cliente entra normal.
   */
  commissionBase?: string | null | undefined;
  legacyTechAmount?: number | string | null | undefined;
  /**
   * F-billing-company-label — nome da empresa faturadora (quem absorve o
   * resíduo): "Euro Storm" na ES, "BR Storms" na BS. Default mantém o
   * comportamento antigo para call sites não migrados.
   */
  companyName?: string;
}

function toNum(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
}

function pickAmount(stored: number | string | null | undefined, fallback: number): number | null {
  // Amount do banco vence quando presente (mesmo que seja 0 explicito).
  if (stored != null && stored !== "") {
    const n = typeof stored === "string" ? Number(stored) : stored;
    if (Number.isFinite(n)) return n;
  }
  return Number.isFinite(fallback) ? fallback : null;
}

export function CommissionCascade({
  facturado,
  clientPct,
  techPct,
  intermPct,
  eurostormPct,
  clientAmount,
  techAmount,
  intermAmount,
  eurostormAmount,
  intermName,
  otherTechAmount,
  otherTechPct,
  otherTechName,
  commissionModel,
  commissionBase,
  legacyTechAmount,
  companyName = "Euro Storm",
}: Props) {
  const t = useT();
  const fact = toNum(facturado);
  let cliente: number;
  let tec: number;
  let interm: number;
  let eurostorm: number;
  if (commissionModel === "legacy") {
    const amount = toNum(legacyTechAmount);
    cliente = 0;
    tec = fact > 0 ? (amount / fact) * 100 : 0;
    interm = 0; // legacy não rastreia intermediário
    eurostorm = Math.max(0, 100 - tec);
  } else {
    cliente = toNum(clientPct);
    tec = toNum(techPct);
    interm = toNum(intermPct);
    // Se backend forneceu eurostormPct, usa direto (correto). Senão, deriva
    // (fallback defensivo — não deve acontecer em complementary persistido).
    eurostorm =
      eurostormPct != null && eurostormPct !== ""
        ? toNum(eurostormPct)
        : Math.max(0, 100 - cliente - tec - interm);
  }
  // F-cascade-use-amounts: prioriza amounts armazenados no banco; cai pro
  // fallback antigo (fact × pct/100) só quando não vem do backend.
  const clienteValue =
    commissionModel === "legacy" ? null : pickAmount(clientAmount, (fact * cliente) / 100);
  const techValueLegacy = commissionModel === "legacy" ? toNum(legacyTechAmount) : 0;
  const techValue =
    commissionModel === "legacy"
      ? fact > 0
        ? techValueLegacy
        : null
      : pickAmount(techAmount, (fact * tec) / 100);
  const intermValue =
    commissionModel === "legacy" ? null : pickAmount(intermAmount, (fact * interm) / 100);
  const eurostormValue =
    commissionModel === "legacy"
      ? fact > 0
        ? Math.max(0, fact - techValueLegacy)
        : null
      : pickAmount(eurostormAmount, (fact * eurostorm) / 100);

  // Em base 'invoiced' (faturado) o cliente não participa do split — a remise
  // já saiu do cálculo (devis − facturé) — então a linha "Cliente" é omitida.
  const hasCliente =
    commissionBase !== "invoiced" && (cliente > 0 || (clienteValue != null && clienteValue > 0));
  const hasInterm =
    interm > 0 ||
    (intermName != null && intermName.length > 0) ||
    (intermValue != null && intermValue > 0);

  return (
    <div className="flex flex-col gap-0.5 text-[11px] font-body tabular-nums">
      {hasCliente && (
        <Row
          label={t("commissionCascade.rowClient")}
          pct={cliente}
          value={fact > 0 ? clienteValue : null}
        />
      )}
      <Row label={t("commissionCascade.rowTech")} pct={tec} value={fact > 0 ? techValue : null} />
      {toNum(otherTechAmount) > 0 && (
        <Row
          label={t("commissionCascade.rowMontage")}
          pct={toNum(otherTechPct)}
          value={toNum(otherTechAmount)}
          title={otherTechName ?? undefined}
        />
      )}
      {hasInterm && (
        <Row
          label={t("commissionCascade.rowIntermediary")}
          pct={interm}
          value={fact > 0 ? intermValue : null}
          title={intermName ?? undefined}
        />
      )}
      <Row
        label={companyName}
        pct={eurostorm}
        value={fact > 0 ? eurostormValue : null}
        emphasised
      />
    </div>
  );
}

function Row({
  label,
  pct,
  value,
  title,
  emphasised,
}: {
  label: string;
  pct: number;
  value: number | null;
  title?: string;
  emphasised?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-[60px_44px_1fr] gap-2 items-baseline ${
        emphasised ? "pt-0.5 border-t border-lime/15" : ""
      }`}
      // Sem title explícito, mostra o rótulo completo no hover (nomes longos
      // de empresa, ex. "Brazilian Storms PDR Service OÜ", são truncados).
      title={title ?? label}
    >
      <span className="text-text-muted text-[10px] truncate min-w-0">{label}</span>
      <span className="text-text-secondary text-[11px] text-right">
        {/* F-pct-decimal (CEO 2026-09-27: "deveria ser 52,5 e aparece 53") —
            o % gravado tem 2 casas; arredondar para inteiro mostrava 53/13. */}
        {PCT_FMT.format(pct)}%
      </span>
      <span
        className={`font-display font-bold text-[12px] text-right ${
          value == null ? "text-text-muted" : "text-text-primary"
        }`}
      >
        {value == null ? "—" : formatMoney(value, { decimals: 2 })}
      </span>
    </div>
  );
}
